import { hasTaskTag, MAX_CONFIRMED_SETTINGS, type JobConfirmSettingsV1, type SettingLookCode } from '@gk/contracts'
import { useMerged } from '../sync/hooks.ts'
import { choiceOptions, type ChoiceOption, type SettingLine, type TaskTypeRow } from './plan.ts'

/** M25-F (docs/M25.md B, rules 3, 4 and 10; ADR-130): THE FINISH'S « RÉGLAGES FAITS », as pure logic (pages/work/SettingsDone.tsx draws it, pages/WorkFinish.tsx
 *  places it and sends job.confirm_settings@1 after job.finish@1, by local_seq). What it knows of the club's words: nothing. It reads only the TAG the type carries
 *  (`confirm_settings`, M16's mechanism) and the type's CHOICE settings — their keys, words and pictures as the club declared them — never what a task or a
 *  setting is called (rule 4).
 *  - the block is asked on a tagged type that has at least one choice setting (at most twelve: the schema's bound), in the order the type declares them;
 *  - **the plan** is the job's own value for a choice setting, when it is one of that setting's words now (a word renamed since is not a plan: it cannot be shown
 *    or picked); the job is never written;
 *  - **when the job planned at least one**: Terminer waits until « Fait comme prévu » (sends the planned words of the settings that had one) or « Modifier » (each
 *    setting as a picker preset to the plan, « Non prévu » where the plan had none) is chosen — one tap when nothing changed;
 *  - **when it planned none** (a job made by hand without settings): « Fait comme prévu » would confirm nothing, so it is not offered; the block is optional
 *    (« Indiquer les réglages faits »), Terminer does not wait, and nothing is sent unless at least one value is picked;
 *  - a setting left « Non prévu » is not sent; an empty pick sends nothing. */
export interface ConfirmSetting {
  key: string; label: string
  /** the club's words in its order, each with its picture or none (`choiceOptions`) */
  options: ChoiceOption[]
  /** the job's planned word, when it is one of the options; else null */
  planned: string | null
}
export interface ConfirmBlock {
  settings: ConfirmSetting[]
  /** at least one setting has a planned word */
  hasPlan: boolean
  /** the plan as lines (the picture and the word), in the declared order: only the planned settings */
  plannedLines: SettingLine[]
}
export type DoneMode = 'planned' | 'modified' | 'indicate'
export interface DoneState {
  /** null until the person chooses */
  mode: DoneMode | null
  /** key → the word picked, or null (« Non prévu »); preset to the plan */
  picks: Record<string, string | null>
  hasPlan: boolean
}

/** The block of a job's finish, or null when the type does not ask (no tag, no choice setting, no type). */
export function confirmBlock(tt: TaskTypeRow | undefined, params: Readonly<Record<string, string | number>>, lang: string): ConfirmBlock | null {
  if (!tt || !hasTaskTag(tt, 'confirm_settings')) return null
  const l = lang.startsWith('en') ? 'en' : 'fr'
  const settings = tt.param_defs.filter((d) => d.kind === 'choice' && (d.choices?.length ?? 0) > 0).slice(0, MAX_CONFIRMED_SETTINGS).map((d): ConfirmSetting => {
    const options = choiceOptions(tt, d.key), v = Object.hasOwn(params, d.key) ? params[d.key] : undefined
    return { key: d.key, label: d.label[l], options, planned: typeof v === 'string' && options.some((o) => o.value === v) ? v : null }
  })
  if (settings.length === 0) return null
  const plannedLines = settings.filter((s) => s.planned !== null).map((s): SettingLine => ({
    key: s.key, label: s.label, value: s.planned!, unit: null, look: s.options.find((o) => o.value === s.planned)?.look ?? null, declared: true,
  }))
  return { settings, hasPlan: plannedLines.length > 0, plannedLines }
}

export const initialState = (b: ConfirmBlock): DoneState => ({ mode: null, picks: Object.fromEntries(b.settings.map((s) => [s.key, s.planned])), hasPlan: b.hasPlan })
/** Choosing a way; « Fait comme prévu » is not one for a job that planned nothing (it would confirm nothing). The picks are kept across the ways. */
export const setMode = (s: DoneState, mode: DoneMode): DoneState => (mode === 'planned' && !s.hasPlan ? s : { ...s, mode })
export const pick = (s: DoneState, key: string, value: string | null): DoneState => ({ ...s, picks: { ...s.picks, [key]: value } })

/** Whether Terminer waits: a block that has a plan and no way chosen yet. No block, or a job that planned nothing, holds nothing. */
export const holdsFinish = (b: ConfirmBlock | null, s: DoneState | null): boolean => !!b && b.hasPlan && !!s && s.mode === null

/** The payload of job.confirm_settings@1, or null when nothing is to be sent (no way chosen, or no value). Only the type's own choice settings and words go. */
export function confirmPayload(b: ConfirmBlock | null, s: DoneState | null, sessionId: string): JobConfirmSettingsV1 | null {
  if (!b || !s || s.mode === null) return null
  const values: Record<string, string> = {}
  for (const set of b.settings) {
    const word = s.mode === 'planned' ? set.planned : Object.hasOwn(s.picks, set.key) ? s.picks[set.key] : null
    if (word && set.options.some((o) => o.value === word)) values[set.key] = word
  }
  return Object.keys(values).length > 0 ? { session_id: sessionId, values } : null
}

/** The rows of ops.job_settings_confirmation a device holds (a crew phone its own, the direction all, the workstation none): the mirror merged with what is pending. */
export interface SettingsConfirmationRow {
  id: string; work_session_id: string; job_id: string; person_id: string
  values: Record<string, string>; looks: Record<string, SettingLookCode | null>; exceptions: string[]; confirmed_at: string
}
export const useSettingsConfirmations = () => useMerged<SettingsConfirmationRow>('job_settings_confirmation', 'ops.job_settings_confirmation')
